// Phase HR-004 Wave 2 — Attendance 3-way reconciliation inbox.
//
// Mill workflow:
//   1. Shift In-charge → Worker Allocation
//   2. Time Office     → Daily Attendance (manual mark)
//   3. Biometric       → final source of truth for payment
//
// Reconciliation engine runs on demand (button) or via scheduler. Any
// (employee, date, shift) row where the 3 sources don't agree is flagged
// DISCREPANCY_PENDING. Two approvals required (GM + HR Executive) before
// payroll picks the row up.
//
// This page is the inbox. Each card shows the 3-source status side-by-side
// so the approver can decide without leaving the screen.

import { useMemo, useState } from 'react'
import {
  Alert, App, Button, DatePicker, Empty, Input, Modal, Space, Tag, Tooltip,
  Typography,
} from 'antd'
import {
  CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined,
  PlayCircleOutlined, ReloadOutlined, SyncOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  reconciliationApi, type DiscrepancyType, type ReconQueueRow,
  type ReconciliationStatus,
} from '@/features/hr/reconciliationApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import { useHrMobilePolish, type ThresholdResult } from '@/shared/styles/hrMobilePolish'

dayjs.extend(relativeTime)
const { Text } = Typography

const STATUS_LABEL: Record<ReconciliationStatus, { label: string; color: string }> = {
  NOT_RECONCILED:       { label: 'Not reconciled',  color: 'default' },
  OK:                   { label: 'OK',              color: 'green' },
  DISCREPANCY_PENDING:  { label: 'Pending',         color: 'red' },
  GM_APPROVED:          { label: 'GM ✓',            color: 'orange' },
  HR_APPROVED:          { label: 'HR ✓',            color: 'orange' },
  FULLY_APPROVED:       { label: 'Fully approved',  color: 'green' },
  REJECTED:             { label: 'Rejected',        color: 'red' },
}

const DISCREPANCY_LABEL: Record<DiscrepancyType, string> = {
  BIOMETRIC_NO_ALLOCATION: 'Punched, not allocated',
  ALLOCATION_NO_BIOMETRIC: 'Allocated, never punched',
  MANUAL_NO_BIOMETRIC:     'Manual PRESENT, no punch',
  BIOMETRIC_NO_MANUAL:     'Punched, manual ABSENT',
  ALLOCATION_NO_MANUAL:    'Allocated, manual blank',
  MIXED:                   'Multiple discrepancies',
}

const STATUS_FILTERS: { value: 'PENDING' | 'PARTIAL' | 'FINALISED' | 'ALL'; label: string }[] = [
  { value: 'PENDING',    label: 'Pending' },
  { value: 'PARTIAL',    label: 'Partial (1 of 2)' },
  { value: 'FINALISED',  label: 'Closed (Approved/Rejected)' },
  { value: 'ALL',        label: 'All' },
]

export default function AttendanceReconciliationPage() {
  useHrMobilePolish()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const canApproveGm = usePermission('attendance.approve_gm')
  const canApproveHr = usePermission('attendance.approve_hr')
  const canRun = usePermission('attendance.reconcile.run')

  const [onDate, setOnDate] = useState<Dayjs>(dayjs())
  const [statusFilter, setStatusFilter] =
    useState<typeof STATUS_FILTERS[number]['value']>('PENDING')
  const [discTypeFilter, setDiscTypeFilter] = useState<DiscrepancyType | 'ALL'>('ALL')
  const [openRow, setOpenRow] = useState<ReconQueueRow | null>(null)
  const [approvalMode, setApprovalMode] = useState<'GM' | 'HR' | 'REJECT' | null>(null)
  const [reason, setReason] = useState('')

  const dateStr = onDate.format('YYYY-MM-DD')

  const queueStatuses = useMemo<ReconciliationStatus[] | undefined>(() => {
    if (statusFilter === 'PENDING')   return ['DISCREPANCY_PENDING']
    if (statusFilter === 'PARTIAL')   return ['GM_APPROVED', 'HR_APPROVED']
    if (statusFilter === 'FINALISED') return ['FULLY_APPROVED', 'REJECTED']
    return undefined
  }, [statusFilter])

  const { data: stats } = useQuery({
    queryKey: ['recon-stats', plantId, dateStr],
    queryFn: () => reconciliationApi.stats({ plant_id: plantId, on_date: dateStr }),
    enabled: !!plantId,
  })

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['recon-queue', plantId, dateStr, statusFilter, discTypeFilter],
    queryFn: () => reconciliationApi.queue({
      plant_id: plantId, on_date: dateStr,
      status_in: queueStatuses,
      discrepancy_type: discTypeFilter === 'ALL' ? undefined : discTypeFilter,
      limit: 300,
    }),
    enabled: !!plantId,
  })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['recon-queue'] })
    qc.invalidateQueries({ queryKey: ['recon-stats'] })
  }

  const runMut = useMutation({
    mutationFn: () => reconciliationApi.run({ on_date: dateStr, plant_id: plantId }),
    onSuccess: (r) => {
      message.success(
        `Reconciled — ${r.ok} OK, ${r.discrepancy_pending} flagged, ${r.stub_created} stub rows created.`,
        6,
      )
      refresh()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(
      e?.response?.data?.detail ?? 'Reconciliation run failed'),
  })

  const approveMut = useMutation({
    mutationFn: (args: { id: string; mode: 'GM' | 'HR' | 'REJECT'; reason: string }) => {
      if (args.mode === 'GM')     return reconciliationApi.approveGm(args.id, args.reason)
      if (args.mode === 'HR')     return reconciliationApi.approveHr(args.id, args.reason)
      return reconciliationApi.reject(args.id, args.reason)
    },
    onSuccess: () => {
      message.success('Saved.')
      refresh()
      setApprovalMode(null); setReason(''); setOpenRow(null)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(
      e?.response?.data?.detail ?? 'Approval failed'),
  })

  return (
    <div className="hr-mobile-root">
      <PageHeader
        title="Attendance Reconciliation"
        subtitle="3-way check: Allocation × Manual × Biometric. GM + HR Exec must both approve any discrepancy before payroll picks it up."
        extra={canRun && (
          <Button
            type="primary" icon={<PlayCircleOutlined />}
            loading={runMut.isPending}
            onClick={() => runMut.mutate()}>
            Reconcile {dateStr}
          </Button>
        )}
      />

      {/* Filter row */}
      <Space wrap style={{ marginBottom: 12 }}>
        <DatePicker value={onDate} allowClear={false}
          onChange={(d) => d && setOnDate(d)} />
        <div className="hr-filter-chips">
          {STATUS_FILTERS.map((s) => (
            <div key={s.value} role="button"
              className={`hr-filter-chip${statusFilter === s.value ? ' hr-filter-chip--active' : ''}`}
              onClick={() => setStatusFilter(s.value)}>
              {s.label}
            </div>
          ))}
        </div>
        <Button icon={<ReloadOutlined />} onClick={refresh} />
      </Space>

      {/* KPI tiles */}
      <div style={{
        display: 'grid', gap: 8, marginBottom: 12,
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      }}>
        <KPITile variant="neutral" value={stats?.total ?? 0} label="In queue" />
        <KPITile
          variant={(stats?.pending ?? 0) > 0 ? 'red' : 'green'}
          value={stats?.pending ?? 0} label="Pending (0 of 2)" />
        <KPITile variant="amber" value={stats?.gm_only_approved ?? 0}
          label="GM only (need HR)" />
        <KPITile variant="amber" value={stats?.hr_only_approved ?? 0}
          label="HR only (need GM)" />
      </div>

      {/* Discrepancy type chip strip — only show counts that exist */}
      {stats && Object.keys(stats.by_discrepancy_type).length > 0 && (
        <div className="hr-filter-chips" style={{ marginBottom: 12 }}>
          <div role="button"
            className={`hr-filter-chip${discTypeFilter === 'ALL' ? ' hr-filter-chip--active' : ''}`}
            onClick={() => setDiscTypeFilter('ALL')}>
            All types
          </div>
          {(Object.keys(stats.by_discrepancy_type) as DiscrepancyType[]).map((t) => (
            <div key={t} role="button"
              className={`hr-filter-chip${discTypeFilter === t ? ' hr-filter-chip--active' : ''}`}
              onClick={() => setDiscTypeFilter(t)}>
              {DISCREPANCY_LABEL[t] ?? t} ({stats.by_discrepancy_type[t]})
            </div>
          ))}
        </div>
      )}

      {!plantId && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Pick a plant in the top bar before reconciling." />
      )}

      {isLoading ? <Empty description="Loading…" image={Empty.PRESENTED_IMAGE_SIMPLE} />
       : rows.length === 0 ? (
          <Empty description={
            statusFilter === 'PENDING'
              ? 'No pending discrepancies for this date.'
              : 'No rows match the filter.'
          } image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )
       : (
        <div style={{
          display: 'grid', gap: 8,
          gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
        }}>
          {rows.map((r) => (
            <ReconCard key={r.id} row={r} onOpen={() => setOpenRow(r)} />
          ))}
        </div>
      )}

      <DetailDrawer
        open={!!openRow}
        row={openRow}
        canApproveGm={canApproveGm}
        canApproveHr={canApproveHr}
        approvalMode={approvalMode}
        setApprovalMode={(m) => { setApprovalMode(m); setReason('') }}
        reason={reason} setReason={setReason}
        onSubmit={() => {
          if (!openRow || !approvalMode) return
          if (!reason.trim()) {
            message.warning('Reason is required.')
            return
          }
          approveMut.mutate({
            id: openRow.id,
            mode: approvalMode,
            reason: reason.trim(),
          })
        }}
        submitting={approveMut.isPending}
        onClose={() => { setOpenRow(null); setApprovalMode(null); setReason('') }}
      />
    </div>
  )
}

// ─── Sub-components ────────────────────────────────────────────────

function KPITile({ variant, value, label }: {
  variant: ThresholdResult; value: number; label: string
}) {
  return (
    <div className={`threshold-tile threshold-tile--${variant}`}>
      <div className="threshold-tile__value">{value}</div>
      <div className="threshold-tile__label">{label}</div>
    </div>
  )
}

function ReconCard({ row, onOpen }: {
  row: ReconQueueRow; onOpen: () => void
}) {
  const statusMeta = STATUS_LABEL[row.reconciliation_status]
  const isCritical = row.reconciliation_status === 'DISCREPANCY_PENDING'
  const stripe = row.reconciliation_status === 'REJECTED' ? '#cf1322'
               : isCritical ? '#cf1322'
               : row.reconciliation_status === 'FULLY_APPROVED' ? '#389e0d'
               : '#d48806'
  return (
    <div className="hr-roster-card" style={{
      flexDirection: 'column', alignItems: 'stretch', cursor: 'pointer',
      borderLeft: `4px solid ${stripe}`,
    }} onClick={onOpen}>
      <Space wrap>
        <Text strong>{row.employee_name ?? '—'}</Text>
        <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>
          {row.emp_code}
        </Text>
        <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
        {row.discrepancy_type && (
          <Tag color="red">{DISCREPANCY_LABEL[row.discrepancy_type]}</Tag>
        )}
      </Space>
      <div style={{ marginTop: 6, fontSize: 11, color: '#8c8c8c' }}>
        {dayjs(row.attendance_date).format('DD MMM')}
        {row.shift_code && <> · Shift {row.shift_code}</>}
        {row.department_name && <> · {row.department_name}</>}
      </div>

      {/* 3-source status row */}
      <div style={{
        display: 'grid', gap: 6, marginTop: 8,
        gridTemplateColumns: 'repeat(3, 1fr)',
      }}>
        <SourceBox label="ALLOC"
          ok={!!row.allocation_id}
          detail={row.allocation_id
            ? `${row.allocation_role ?? 'OPERATOR'}${row.allocation_machine_id ? ' · m/c set' : ''}`
            : 'Not allocated'} />
        <SourceBox label="MANUAL"
          ok={row.manual_status === 'PRESENT' || row.manual_status === 'HALF_DAY'}
          detail={row.manual_status} />
        <SourceBox label="BIOMETRIC"
          ok={row.biometric_punched}
          detail={row.biometric_punched
            ? (row.biometric_first_in ? dayjs(row.biometric_first_in).format('HH:mm') : 'punched')
            : 'No punch'} />
      </div>

      {/* Approval progress */}
      {(row.gm_approval_at || row.hr_approval_at) && (
        <div style={{ marginTop: 8, fontSize: 11, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {row.gm_approval_at && (
            <Tag color="orange">GM: {row.gm_approval_by_name ?? '—'} ({dayjs(row.gm_approval_at).fromNow()})</Tag>
          )}
          {row.hr_approval_at && (
            <Tag color="orange">HR: {row.hr_approval_by_name ?? '—'} ({dayjs(row.hr_approval_at).fromNow()})</Tag>
          )}
        </div>
      )}
    </div>
  )
}

function SourceBox({ label, ok, detail }: {
  label: string; ok: boolean; detail: string
}) {
  const bg = ok ? '#f6ffed' : '#fff2f0'
  const border = ok ? '#b7eb8f' : '#ffa39e'
  const iconColor = ok ? '#52c41a' : '#cf1322'
  return (
    <div style={{
      padding: 8, borderRadius: 6,
      background: bg, border: `1px solid ${border}`,
      fontSize: 11,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4,
                    fontWeight: 600, letterSpacing: 0.5, color: '#595959' }}>
        {ok ? <CheckCircleOutlined style={{ color: iconColor }} />
            : <CloseCircleOutlined style={{ color: iconColor }} />}
        {label}
      </div>
      <div style={{ marginTop: 2, fontFamily: 'monospace' }}>{detail}</div>
    </div>
  )
}

function DetailDrawer({
  open, row, canApproveGm, canApproveHr,
  approvalMode, setApprovalMode, reason, setReason,
  onSubmit, submitting, onClose,
}: {
  open: boolean; row: ReconQueueRow | null
  canApproveGm: boolean; canApproveHr: boolean
  approvalMode: 'GM' | 'HR' | 'REJECT' | null
  setApprovalMode: (m: 'GM' | 'HR' | 'REJECT' | null) => void
  reason: string; setReason: (s: string) => void
  onSubmit: () => void; submitting: boolean
  onClose: () => void
}) {
  if (!row) return null
  const status = row.reconciliation_status
  const showGm = canApproveGm && !row.gm_approval_at
    && status !== 'FULLY_APPROVED' && status !== 'REJECTED'
  const showHr = canApproveHr && !row.hr_approval_at
    && status !== 'FULLY_APPROVED' && status !== 'REJECTED'
  const showReject = (canApproveGm || canApproveHr)
    && status !== 'REJECTED' && status !== 'FULLY_APPROVED'

  return (
    <Modal
      open={open} onCancel={onClose}
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
          <span>{row.employee_name} · {row.emp_code}</span>
        </Space>
      }
      width={600}
      footer={null}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {dayjs(row.attendance_date).format('DD MMM YYYY')}
            {row.shift_code && <> · Shift {row.shift_code}</>}
            {row.department_name && <> · {row.department_name}</>}
          </Text>
        </div>

        {row.discrepancy_type && (
          <Alert type="warning" showIcon
            message={DISCREPANCY_LABEL[row.discrepancy_type]}
            description={describeDiscrepancy(row)} />
        )}

        {/* 3-source detail grid */}
        <div style={{
          display: 'grid', gap: 8,
          gridTemplateColumns: '1fr 1fr 1fr',
        }}>
          <SourceDetail title="Allocation"
            ok={!!row.allocation_id}
            lines={[
              row.allocation_id
                ? `Role: ${row.allocation_role ?? 'OPERATOR'}`
                : 'No allocation row',
              row.allocation_machine_id ? 'Machine: assigned' : 'No machine',
            ]} />
          <SourceDetail title="Manual"
            ok={row.manual_status === 'PRESENT' || row.manual_status === 'HALF_DAY'}
            lines={[`Status: ${row.manual_status}`]} />
          <SourceDetail title="Biometric"
            ok={row.biometric_punched}
            lines={[
              row.biometric_punched ? 'Punched' : 'No punch detected',
              row.biometric_first_in
                ? `IN: ${dayjs(row.biometric_first_in).format('HH:mm')}` : '',
              row.biometric_last_out
                ? `OUT: ${dayjs(row.biometric_last_out).format('HH:mm')}` : '',
            ].filter(Boolean)} />
        </div>

        {row.approval_reason && (
          <div style={{ background: '#fafafa', padding: 10, borderRadius: 6 }}>
            <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Approval trail
            </Text>
            <pre style={{ fontSize: 11, margin: 0, whiteSpace: 'pre-wrap' }}>
              {row.approval_reason}
            </pre>
          </div>
        )}

        {row.rejection_reason && (
          <Alert type="error" showIcon
            message="Rejected"
            description={row.rejection_reason} />
        )}

        {/* Action buttons */}
        {approvalMode === null ? (
          <Space wrap>
            {showGm && (
              <Tooltip title="Sign off as GM / Plant Head">
                <Button type="primary" onClick={() => setApprovalMode('GM')}>
                  Approve as GM
                </Button>
              </Tooltip>
            )}
            {showHr && (
              <Tooltip title="Sign off as HR Executive">
                <Button type="primary" onClick={() => setApprovalMode('HR')}>
                  Approve as HR
                </Button>
              </Tooltip>
            )}
            {showReject && (
              <Button danger onClick={() => setApprovalMode('REJECT')}>
                Reject
              </Button>
            )}
            <Button onClick={onClose}>Close</Button>
          </Space>
        ) : (
          <div>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              {approvalMode === 'REJECT' ? 'Rejection reason' : 'Approval reason'} (required)
            </Text>
            <Input.TextArea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                approvalMode === 'REJECT'
                  ? 'Why is this row rejected? e.g. "proxy punch confirmed by camera review"'
                  : 'Brief note: what makes this row payable despite the discrepancy?'
              }
            />
            <Space style={{ marginTop: 8 }}>
              <Button
                type={approvalMode === 'REJECT' ? undefined : 'primary'}
                danger={approvalMode === 'REJECT'}
                loading={submitting}
                onClick={onSubmit}
              >
                Save {approvalMode === 'REJECT' ? 'rejection' : 'approval'}
              </Button>
              <Button onClick={() => setApprovalMode(null)}>Back</Button>
            </Space>
          </div>
        )}
      </Space>
    </Modal>
  )
}

function SourceDetail({ title, ok, lines }: {
  title: string; ok: boolean; lines: string[]
}) {
  return (
    <div style={{
      padding: 10, borderRadius: 8,
      background: ok ? '#f6ffed' : '#fff2f0',
      border: `1px solid ${ok ? '#b7eb8f' : '#ffa39e'}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4,
        fontWeight: 600, marginBottom: 4,
      }}>
        {ok ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
            : <CloseCircleOutlined style={{ color: '#cf1322' }} />}
        {title}
      </div>
      {lines.map((l, i) => (
        <div key={i} style={{ fontSize: 11, fontFamily: 'monospace' }}>{l}</div>
      ))}
    </div>
  )
}

function describeDiscrepancy(r: ReconQueueRow): string {
  switch (r.discrepancy_type) {
    case 'BIOMETRIC_NO_ALLOCATION':
      return 'Worker punched in but supervisor never allocated them to a machine. Could be a sweeper / reliever who roamed without formal allocation — or someone who shouldn\'t have been on the floor.'
    case 'ALLOCATION_NO_BIOMETRIC':
      return 'Supervisor allocated this worker but no biometric punch was recorded. Either a no-show, a missed punch, or biometric reader failure.'
    case 'MANUAL_NO_BIOMETRIC':
      return 'Time office marked PRESENT but biometric shows no punch. Verify if reader was working that morning.'
    case 'BIOMETRIC_NO_MANUAL':
      return 'Biometric shows a punch but time office marked ABSENT. Possible proxy punch or time-office error.'
    case 'ALLOCATION_NO_MANUAL':
      return 'Worker was allocated but time office hasn\'t yet marked manual attendance.'
    case 'MIXED':
      return 'Multiple sources disagree on the same row. Investigate each side individually.'
    default:
      return ''
  }
}
